// frontend/src/services/CsrfTokenService.js
import { useAuthStore } from '@/stores/auth';
import router from '@/router';

class CsrfTokenService {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  // Get current CSRF token
  getCurrentToken() {
    // Import apiClient dynamically to avoid circular dependency
    try {
      const apiClient = require('./api').default;
      return apiClient.defaults.headers.common['X-CSRFToken'];
    } catch (error) {
      console.error('Error getting current CSRF token:', error);
      return null;
    }
  }

  // Process the queue of failed requests
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  // Add request to failed queue
  addToFailedQueue() {
    return new Promise((resolve, reject) => {
      this.failedQueue.push({ resolve, reject });
    });
  }

  // Check if error is CSRF expiry
  isCsrfExpiredError(error) {
    return error.response?.data?.error === 'csrf_expired';
  }

  // Handle CSRF token expiry
  async handleCsrfExpiry(originalRequest, retryCount = 0) {
    console.log(`🔄 Handling CSRF token expiry (attempt ${retryCount + 1}/${this.maxRetries})`);

    if (retryCount >= this.maxRetries) {
      console.error('❌ Max CSRF refresh attempts reached');
      this.handleMaxRetriesExceeded();
      return false;
    }

    if (this.isRefreshing) {
      // If already refreshing, queue this request
      return this.addToFailedQueue().then(() => {
        return this.retryRequest(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    this.isRefreshing = true;

    try {
      // Wait a bit before retrying to avoid overwhelming the server
      if (retryCount > 0) {
        await this.delay(this.retryDelay * retryCount);
      }

      const success = await this.refreshToken();
      
      if (success) {
        this.processQueue(null, true);
        return this.retryRequest(originalRequest);
      } else {
        this.processQueue(new Error('Failed to refresh CSRF token'), null);
        return false;
      }
    } catch (error) {
      console.error('❌ Error during CSRF token refresh:', error);
      this.processQueue(error, null);
      
      // If refresh failed, try again with exponential backoff
      if (retryCount < this.maxRetries - 1) {
        return this.handleCsrfExpiry(originalRequest, retryCount + 1);
      } else {
        this.handleMaxRetriesExceeded();
        return false;
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  // Refresh CSRF token
  async refreshToken() {
    try {
      console.log('🔄 Refreshing CSRF token...');
      
      // Import here to avoid circular dependency
      const { fetchAndSetCsrfToken } = await import('./AuthService');
      const success = await fetchAndSetCsrfToken();
      
      if (success) {
        console.log('✅ CSRF token refreshed successfully');
        return true;
      } else {
        console.error('❌ Failed to refresh CSRF token');
        return false;
      }
    } catch (error) {
      console.error('❌ Error refreshing CSRF token:', error);
      return false;
    }
  }

  // Retry the original request
  async retryRequest(originalRequest) {
    try {
      console.log('🔄 Retrying original request with new CSRF token...');
      const apiClient = (await import('./api')).default;
      return await apiClient(originalRequest);
    } catch (error) {
      console.error('❌ Error retrying request:', error);
      return Promise.reject(error);
    }
  }

  // Handle max retries exceeded
  handleMaxRetriesExceeded() {
    console.error('❌ Max CSRF refresh attempts exceeded. User needs to re-authenticate.');
    
    // Show user notification
    this.showUserNotification();
    
    // Clear auth state and redirect to login
    const authStore = useAuthStore();
    authStore.logout();
  }

  // Show user notification about session expiry
  showUserNotification() {
    // You can integrate this with your notification system (Vuetify Snackbar, etc.)
    console.warn('⚠️ Your session has expired. Please log in again.');
    
    // Example with Vuetify Snackbar (if you're using Vuetify)
    // this.$store.dispatch('showNotification', {
    //   message: 'Your session has expired. Please log in again.',
    //   type: 'warning',
    //   timeout: 5000
    // });
  }

  // Utility function for delays
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Check if user should be redirected to login
  shouldRedirectToLogin(error) {
    // Check for various authentication-related errors
    const authErrors = ['csrf_expired', 'unauthorized', 'forbidden', 'token_expired'];
    return authErrors.includes(error.response?.data?.error) || 
           error.response?.status === 401 || 
           error.response?.status === 403;
  }

  // Handle authentication errors
  async handleAuthError(error) {
    if (this.shouldRedirectToLogin(error)) {
      console.log('🔐 Authentication error detected, redirecting to login...');
      
      const authStore = useAuthStore();
      
      // Clear current session
      authStore.user = null;
      authStore.isLoggedIn = false;
      
      // Redirect to login with return URL
      const currentRoute = router.currentRoute.value;
      const returnUrl = currentRoute.fullPath;
      
      router.push({
        name: 'Login',
        query: { redirect: returnUrl }
      });
      
      return true;
    }
    return false;
  }
}

// Create singleton instance
const csrfTokenService = new CsrfTokenService();

export default csrfTokenService; 